import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createActivity, updateActivity, fetchActivityById } from './activitySlice';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import log from '../../logger';
import ReactForm from '../../components/ReactForm';
import { paths } from '../../routes/helpers';
import { getImageUrl, toLocalUploadPath } from '../../utils/urlUtils';
import ImageGallery from '../../components/ImageGallery';

const ActivityCreateEdit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { activityId } = useParams();
  const loading = useSelector((state) => state.activity.loading);
  const error = useSelector((state) => state.activity.error);
  const activity = useSelector((state) => 
    activityId ? state.activity.items.find(a => a._id === activityId) : null
  );
  
  const [imageFiles, setImageFiles] = useState([]);
  const [existingImage, setExistingImage] = useState('');

  useEffect(() => {
    if (activityId) {
      dispatch(fetchActivityById(activityId));
    }
  }, [activityId, dispatch]);

  useEffect(() => {
    if (activity && activity.image) {
      setExistingImage(getImageUrl(activity.image));
    }
  }, [activity]);

  const handleImageChange = (event) => {
    const files = Array.from(event.target.files);
    setImageFiles(files);
  };

  const handleRemoveImage = (index) => {
    setImageFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = () => {
    setExistingImage('');
  };

  const handleSubmit = async (formData) => {
    try {
      const processedData = new FormData();
      processedData.append('label', formData.label);
      processedData.append('status', parseInt(formData.status));
      
      if (imageFiles.length > 0) {
        processedData.append('image', imageFiles[0]);
      } else if (existingImage && activityId) {
        // Keep existing image
        processedData.append('existingImage', toLocalUploadPath(existingImage));
      }

      if (activityId) {
        await dispatch(updateActivity({ id: activityId, formData: processedData })).unwrap();
        toast.success('Activity updated successfully!');
        log.info('Activity updated:', formData.label);
      } else {
        await dispatch(createActivity(processedData)).unwrap();
        toast.success('Activity created successfully!');
        log.info('Activity created:', formData.label);
      }
      navigate(paths.private.activity.list());
    } catch (err) {
      if (err.message.includes('Unauthorized access')) {
        toast.error('You do not have permission to perform this action.');
      } else {
        toast.error(err.message || 'An error occurred');
      }
      log.error('Error creating/updating activity:', err);
    }
  };

  const fields = [
    {
      name: 'label',
      label: 'Label',
      type: 'text',
      required: true,
      placeholder: 'Enter activity label'
    },
    {
      name: 'image',
      label: 'Image',
      type: 'file',
      accept: 'image/*',
      required: !activityId || (!existingImage && imageFiles.length === 0),
      onChange: handleImageChange,
      preview: (
        <ImageGallery 
          existingImages={imageFiles.length > 0 ? [] : existingImage ? [existingImage] : []}
          imageFiles={imageFiles}
          onRemoveExisting={handleRemoveExistingImage}
          onRemoveNew={handleRemoveImage}
          loading={loading && activity?.image && !existingImage}
        />
      )
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      defaultValue: "0",
      options: [
        { value: "0", label: 'Inactive' },
        { value: "1", label: 'Active' },
        { value: "2", label: 'Suspended' }
      ]
    }
  ];

  const defaultValues = activity ? {
    label: activity.label,
    image: [],
    status: activity.status.toString()
  } : {
    label: '',
    image: [],
    status: "1"
  };

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">{activityId ? 'Edit Activity' : 'Create Activity'}</h2>
      <ReactForm
        fields={fields}
        onSubmit={handleSubmit}
        loading={loading}
        error={error}
        defaultValues={defaultValues}
        submitText={activityId ? 'Update Activity' : 'Create Activity'}
      />
    </div>
  );
};

export default ActivityCreateEdit; 